import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trophy, Target, Activity, TrendingUp, Shield, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AWARD_ICONS = { mvp: Trophy, scoring: Target, rebounding: Activity, assists: TrendingUp, dpoy: Shield };
const AWARD_COLORS = { mvp: 'bg-amber-500', scoring: 'bg-red-500', rebounding: 'bg-blue-500', assists: 'bg-purple-500', dpoy: 'bg-green-500' };

const careerHref = (shareToken, teamName, playerName, seasonId, eventId) => {
  const params = new URLSearchParams({ team: teamName, name: playerName });
  if (seasonId) params.append('season_id', seasonId);
  if (eventId) params.append('event_id', eventId);
  return `/stats/leaderboard/${shareToken}/career?${params.toString()}`;
};

export default function PublicLeaderboard() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');

  const fetchData = (eventId = '', seasonId = '') => {
    setLoading(true);
    const params = new URLSearchParams();
    if (eventId) params.append('event_id', eventId);
    if (seasonId) params.append('season_id', seasonId);
    const qs = params.toString();
    const url = qs
      ? `${API_URL}/api/stats/leaderboard/${shareToken}?${qs}`
      : `${API_URL}/api/stats/leaderboard/${shareToken}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(setData)
      .catch(() => setError('Leaderboard not found or link has been revoked.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [shareToken]);

  const handleEventChange = (eventId) => {
    setSelectedEvent(eventId);
    fetchData(eventId, selectedSeason);
  };
  const handleSeasonChange = (seasonId) => {
    setSelectedSeason(seasonId);
    fetchData(selectedEvent, seasonId);
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center text-center px-4">
      <div>
        <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 text-lg">{error}</p>
        <Link to="/" className="text-amber-400 text-sm hover:underline mt-4 inline-block">Go to Schedule Guru</Link>
      </div>
    </div>
  );

  const { branding = {}, games_count, per_rankings, stat_leaders, awards, recent_games, events, seasons } = data;
  const title = branding.title || 'Season Leaderboard';
  const subtitle = branding.subtitle || '';
  const accentColor = branding.accent_color || '#F59E0B';
  const logoUrl = branding.logo_url || '';
  // Server-side fallback: when a leaderboard share doesn't have a custom
  // `logo_url` configured, the user's profile organization_logo path comes
  // back as `org_logo_path`. We render that via the /api/files proxy.
  const orgLogoPath = branding.org_logo_path || '';
  const heroLogoSrc = logoUrl
    ? logoUrl
    : (orgLogoPath ? `${API_URL}/api/files/${orgLogoPath}` : '');

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      {/* Header */}
      <header className="bg-[#00247D] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 text-center">
          {heroLogoSrc ? (
            <img src={heroLogoSrc} alt="" className="h-12 mx-auto mb-3 object-contain" data-testid="leaderboard-hero-logo" />
          ) : (
            <Link to="/">
              <img src="/logo-transparent.png" alt="Schedule Guru" className="h-10 mx-auto mb-3 opacity-80" />
            </Link>
          )}
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed', color: accentColor }} data-testid="leaderboard-title">
            {title}
          </h1>
          {subtitle && <p className="text-sm text-white/70 mt-1">{subtitle}</p>}
          <p className="text-sm text-white/50 mt-1">{games_count} game{games_count !== 1 ? 's' : ''} completed</p>
          {events && events.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <Select value={selectedEvent || 'all'} onValueChange={v => handleEventChange(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px] bg-white/10 border-white/20 text-white hover:bg-white/15 focus:ring-amber-400" data-testid="event-filter-select">
                  <SelectValue placeholder="All Leagues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leagues</SelectItem>
                  {events.map(ev => (
                    <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {seasons && seasons.length > 0 && (
                <Select value={selectedSeason || 'all'} onValueChange={v => handleSeasonChange(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[200px] bg-white/10 border-white/20 text-white hover:bg-white/15 focus:ring-amber-400" data-testid="season-filter-select">
                    <SelectValue placeholder="All Seasons" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Seasons</SelectItem>
                    {seasons.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          {(!events || events.length === 0) && seasons && seasons.length > 0 && (
            <div className="mt-3 flex justify-center">
              <Select value={selectedSeason || 'all'} onValueChange={v => handleSeasonChange(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px] bg-white/10 border-white/20 text-white hover:bg-white/15 focus:ring-amber-400" data-testid="season-filter-select">
                  <SelectValue placeholder="All Seasons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Seasons</SelectItem>
                  {seasons.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">

        {/* Awards Banner */}
        {awards.length > 0 && (
          <section>
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Season Awards</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="awards-banner">
              {awards.map(a => {
                const Icon = AWARD_ICONS[a.key] || Trophy;
                const bg = AWARD_COLORS[a.key] || 'bg-slate-500';
                return (
                  <Link key={a.key} to={careerHref(shareToken, a.winner.team_name, a.winner.name, selectedSeason, selectedEvent)}
                    className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 hover:border-amber-500 hover:scale-[1.02] transition-all"
                    data-testid={`award-${a.key}`}>
                    <div className={`${bg} px-3 py-2 flex items-center gap-2`}>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-xs font-bold text-white uppercase">{a.title}</span>
                    </div>
                  <div className="p-3 flex items-center gap-3">
                    {a.winner.headshot_path ? (
                      <img src={`${API_URL}/api/files/${a.winner.headshot_path}`} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-slate-600 shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-700 text-amber-400 flex items-center justify-center font-bold text-sm shrink-0">
                        #{a.winner.jersey}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{a.winner.name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1">
                        {a.winner.team_logo && <img src={`${API_URL}/api/files/${a.winner.team_logo}`} alt="" className="w-3 h-3 rounded-full object-cover" />}
                        {a.winner.team_name}
                      </div>
                    </div>
                    <div className="ml-auto text-lg font-black text-amber-400">{a.stat_value}</div>
                  </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Stat Leaders */}
        {Object.keys(stat_leaders).length > 0 && (
          <section>
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Stat Leaders</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="stat-leaders">
              {Object.entries(stat_leaders).map(([key, { label, leaders }]) => (
                <div key={key} className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                  <h3 className="text-[10px] font-bold uppercase text-slate-400 mb-3 tracking-wider">{label}</h3>
                  <div className="space-y-2">
                    {leaders.map((p, i) => (
                      <Link key={i} to={careerHref(shareToken, p.team_name, p.name, selectedSeason, selectedEvent)}
                        className="flex items-center gap-2 text-sm hover:bg-slate-700/50 rounded-md px-1 py-1 -mx-1 transition-colors"
                        data-testid={`stat-leader-link-${key}-${i}`}>
                        <span className={`w-5 text-center font-bold ${i === 0 ? 'text-amber-400' : 'text-slate-500'}`}>{i + 1}</span>
                        {p.headshot_path ? (
                          <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                        ) : (
                          <span className="font-mono text-xs text-slate-500">#{p.jersey}</span>
                        )}
                        <span className="flex-1 truncate hover:text-amber-300 hover:underline underline-offset-2">{p.name}</span>
                        <span className="font-bold text-amber-400">{p.total}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* PER Rankings Table */}
        {per_rankings.length > 0 && (
          <section>
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Efficiency Rankings (PER)</h2>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="w-full text-left" data-testid="per-table">
                    <TableHeader>
                      <TableRow className="text-[10px] font-bold uppercase text-slate-400 border-b border-slate-700 hover:bg-transparent">
                        <TableHead className="px-3 py-3 text-center w-8 text-slate-400">RK</TableHead>
                        <TableHead className="px-3 py-3 w-8 text-slate-400">#</TableHead>
                        <TableHead className="px-3 py-3 text-slate-400">Player</TableHead>
                        <TableHead className="px-3 py-3 text-slate-400">Team</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">GP</TableHead>
                        <TableHead className="px-3 py-3 text-center text-amber-400">PER</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">PPG</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">RPG</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">APG</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">SPG</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">BPG</TableHead>
                        <TableHead className="px-3 py-3 text-center text-slate-400">FG%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {per_rankings.map((p, i) => (
                        <TableRow key={i} className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${i < 3 ? 'bg-amber-500/5' : ''}`}>
                          <TableCell className="px-3 py-2 text-center text-xs font-bold text-slate-500">{i + 1}</TableCell>
                          <TableCell className="px-3 py-2 font-mono text-xs text-slate-500">{p.jersey}</TableCell>
                          <TableCell className="px-3 py-2 text-sm font-semibold text-slate-200">
                            <Link to={careerHref(shareToken, p.team_name, p.name, selectedSeason, selectedEvent)}
                              className="inline-flex items-center gap-2 hover:text-amber-300 hover:underline underline-offset-2"
                              data-testid={`per-player-link-${i}`}>
                              {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />}
                              <span>{p.name}</span>
                            </Link>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm text-slate-400">
                            <span className="inline-flex items-center gap-1.5">
                              {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" />}
                              {p.team_name}
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-400">{p.gp}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center font-black text-amber-400">{p.per}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-200">{p.ppg}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-200">{p.rpg}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-200">{p.apg}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-200">{p.spg}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-200">{p.bpg}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-center text-slate-200">{p.fg_pct}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Recent Games */}
        {recent_games.length > 0 && (
          <section>
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Recent Games</h2>
            <div className="space-y-2" data-testid="recent-games">
              {recent_games.map((g, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-slate-800 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-3 min-w-0">
                    <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="text-xs text-slate-400 shrink-0">{g.date}</span>
                    <span className="text-sm font-medium truncate">{g.name}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-sm flex-wrap">
                    {g.team_a_logo && <img src={`${API_URL}/api/files/${g.team_a_logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />}
                    <span className="text-slate-400 truncate max-w-[100px] sm:max-w-none">{g.team_a}</span>
                    <span className="font-bold text-lg">{g.score_a}</span>
                    <span className="text-slate-500">-</span>
                    <span className="font-bold text-lg">{g.score_b}</span>
                    <span className="text-slate-400 truncate max-w-[100px] sm:max-w-none">{g.team_b}</span>
                    {g.team_b_logo && <img src={`${API_URL}/api/files/${g.team_b_logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center pt-8 pb-4 border-t border-slate-800">
          <Link to="/" className="text-slate-500 text-xs hover:text-white transition-colors">
            Powered by <span className="font-semibold">scheduleguru.ws</span>
          </Link>
        </footer>
      </div>
    </div>
  );
}
